import { Text, useWindowDimensions, type StyleProp, type TextStyle } from 'react-native';
import { colors, fontFamily, type, typeCompact } from '~/ui/tokens';

interface SplitHeadingProps {
  /** Optional leading text before the emphasised keyword. */
  pre?: string;
  /** The emphasised keyword — rendered in the Luxury-Escapes editorial style:
   * a serif italic in the brand colour. */
  accent: string;
  rest?: string;
  size?: 'h1' | 'h2';
  style?: StyleProp<TextStyle>;
}

// A serif italic keyword against the sans headline — the Luxury Escapes
// "Today's top *exclusive* offers" treatment. Georgia is a safe web serif;
// react-native-web passes fontFamily straight to CSS. The accent deliberately
// sets no fontSize: it inherits the heading's semantic size from the parent
// Text so normalising a heading never desynchronises the italic from the
// sans-serif words it sits between.
export const accentTextStyle: TextStyle = {
  fontFamily: 'Georgia',
  fontStyle: 'italic',
  color: colors.brand,
};

// Weight and letter spacing are the heading's own, preserved per size; the
// semantic tokens contribute size and line height only.
const headingStyle: Record<'h1' | 'h2', TextStyle> = {
  h1: { fontFamily: fontFamily.bold, letterSpacing: -0.5 },
  h2: { fontFamily: fontFamily.bold, letterSpacing: -0.3 },
};

export function SplitHeading({ pre, accent, rest, size = 'h2', style }: SplitHeadingProps) {
  const { width } = useWindowDimensions();
  const compact = width < 768;
  const metrics =
    size === 'h1'
      ? compact
        ? typeCompact.pageTitle
        : type.pageTitle
      : compact
        ? typeCompact.sectionTitle
        : type.sectionTitle;

  return (
    <Text style={[headingStyle[size], metrics, style]}>
      {pre ? <Text style={{ color: colors.ink }}>{pre}</Text> : null}
      <Text style={accentTextStyle}>{accent}</Text>
      {rest ? <Text style={{ color: colors.ink }}>{rest}</Text> : null}
    </Text>
  );
}
